import { useEffect, useState, useCallback } from 'react';
import { useAuthInfo } from '@propelauth/react';
import { getNotifications, markNotificationAsSeen } from 'src/api/notifications';
import { showMessage } from '@fuse/core/FuseMessage/fuseMessageSlice';
import { useAppDispatch } from 'app/store/hooks';
import { useAxiosInstance } from 'src/api/axiosInstance';
import { useTranslation } from 'react-i18next';
import { shouldAllowNotifications, getDeviceInfo } from 'src/app/utils/notificationUtils';

interface Notification {
  id: number;
  org_id: string;
  org_name: string;
  user_id: string;
  user_name: string;
  notification_type: string;
  content: string;
  language_sent: string;
  timestamp: string;
  seen: boolean;
  seen_at?: string;
  metadata?: any;
}

export const useNotifications = () => {
  const dispatch = useAppDispatch();
  const { user, accessToken } = useAuthInfo();
  const axiosInstance = useAxiosInstance();
  const { t } = useTranslation();
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(true);

  // Fetch notifications from backend API
  const fetchNotifications = useCallback(async () => {
    if (!user || !accessToken) return;

    setLoading(true);
    try {
      const data = await getNotifications(axiosInstance);
      
      // Handle both array and object responses
      const notificationsList = Array.isArray(data) ? data : (data?.notifications || []);
      setNotifications(notificationsList);
      
      // Calculate unread count
      const unread = notificationsList.filter((n: Notification) => !n.seen).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setNotifications([]); // Ensure we always have an array
    } finally {
      setLoading(false);
    }
  }, [user, accessToken]);

  // Check if browser notifications are supported
  useEffect(() => {
    const checkSupport = async () => {
      console.log('🔔 Checking notification support...');
      
      if (!('Notification' in window)) {
        setIsSupported(false);
        console.log('❌ This browser does not support notifications');
        return;
      }

      console.log('✅ Notifications are supported');
      console.log('📱 Current permission:', Notification.permission);
      setPermission(Notification.permission);
    };

    checkSupport();
  }, []);

  // Request notification permission (browser native only, no FCM)
  const requestPermission = useCallback(async () => {
    if (!isSupported || !user || !accessToken) return;

    // BLOCK DESKTOP DEVICES - Don't request browser notifications on desktop
    if (!shouldAllowNotifications()) {
      const deviceInfo = getDeviceInfo();
      console.log('🖥️ Blocking notification permission request on desktop device:', deviceInfo);
      
      dispatch(showMessage({
        message: t('DESKTOP_NOTIFICATIONS_NOT_AVAILABLE') || 'Notifications are not available on desktop devices for better user experience.',
        variant: 'info',
      }));
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);

      if (permission === 'granted') {
        dispatch(showMessage({
          message: t('NOTIFICATIONS_ENABLED_SUCCESS'),
          variant: 'success',
        }));
      } else if (permission === 'denied') {
        dispatch(showMessage({
          message: t('NOTIFICATIONS_BLOCKED_MESSAGE'),
          variant: 'warning',
        }));
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      dispatch(showMessage({
        message: t('FAILED_TO_ENABLE_NOTIFICATIONS'),
        variant: 'error',
      }));
    }
  }, [isSupported, user, accessToken, dispatch, t]);

  // Mark notification as seen
  const markAsSeen = useCallback(async (notificationId: number) => {
    if (!accessToken) return;

    try {
      await markNotificationAsSeen(notificationId, axiosInstance);
      
      // Update local state
      setNotifications(prev => 
        Array.isArray(prev) ? prev.map(n => 
          n.id === notificationId ? { ...n, seen: true, seen_at: new Date().toISOString() } : n
        ) : []
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as seen:', error);
    }
  }, [accessToken]);

  // Mark all notifications as seen
  const markAllAsSeen = useCallback(async () => {
    if (!accessToken) return;

    try {
      // Get all unseen notification IDs
      const unseenIds = Array.isArray(notifications) ? notifications.filter(n => !n.seen).map(n => n.id) : [];
      
      // Mark all as seen
      await Promise.all(unseenIds.map(id => markNotificationAsSeen(id, axiosInstance)));
      
      // Update local state
      setNotifications(prev => 
        Array.isArray(prev) ? prev.map(n => ({ ...n, seen: true, seen_at: new Date().toISOString() })) : []
      );
      
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as seen:', error);
    }
  }, [notifications, accessToken]);

  // Format room assignment messages for display
  const formatRoomAssignmentMessage = useCallback((payload: any) => {
    const { notification, data } = payload;
    const notificationType = data?.notificationType;
    
    if (notificationType === 'room_assigned') {
      return {
        title: t('NEW_ROOM_ASSIGNMENT'),
        message: data?.roomNumbers ? 
          t('ROOMS_ASSIGNED_TO_YOU', { rooms: data.roomNumbers }) : 
          notification?.body || t('YOU_HAVE_NEW_ROOM_ASSIGNMENTS')
      };
    }
    
    if (notificationType === 'room_reassigned') {
      return {
        title: t('ROOM_REASSIGNMENT'),
        message: data?.roomNumbers ? 
          t('ROOMS_REASSIGNED_MESSAGE', { 
            rooms: data.roomNumbers,
            cleaner: data?.newCleanerName || data?.oldCleanerName || t('ANOTHER_CLEANER')
          }) : 
          notification?.body || t('ROOM_ASSIGNMENTS_CHANGED')
      };
    }
    
    // Default formatting
    return {
      title: notification?.title || t('NOTIFICATION'),
      message: notification?.body || t('YOU_HAVE_NEW_NOTIFICATION')
    };
  }, [t]);

  // Initial fetch when user is available
  useEffect(() => {
    if (user && accessToken) {
      fetchNotifications();
    }
  }, [user, accessToken, fetchNotifications]);

  return {
    permission,
    isSupported,
    fcmToken: null, // No longer using FCM tokens
    notifications,
    unreadCount,
    loading,
    requestPermission,
    fetchNotifications,
    markAsSeen,
    markAllAsSeen,
    formatRoomAssignmentMessage,
  };
};